/**
 * IncidentCard Component
 *
 * Card display for incident reports.
 * Shows severity badge, type icon, status, and metadata.
 *
 * @module pages/technician/incidents/IncidentCard
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  FiTool,
  FiShield,
  FiPackage,
  FiClock,
  FiUsers,
  FiHelpCircle,
  FiCloud,
  FiImage,
  FiChevronRight,
  FiMapPin,
} from 'react-icons/fi';
import {
  INCIDENT_TYPE,
  INCIDENT_SEVERITY,
  INCIDENT_STATUS,
  INCIDENT_TYPE_LABELS,
} from '../../../utils/constants';
import './IncidentReportForm.css';

const TYPE_ICONS = {
  [INCIDENT_TYPE.EQUIPMENT_FAILURE]: FiTool,
  [INCIDENT_TYPE.SAFETY]: FiShield,
  [INCIDENT_TYPE.MATERIAL_ISSUE]: FiPackage,
  [INCIDENT_TYPE.DELAY]: FiClock,
  [INCIDENT_TYPE.CLIENT_ISSUE]: FiUsers,
  [INCIDENT_TYPE.WEATHER]: FiCloud,
  [INCIDENT_TYPE.OTHER]: FiHelpCircle,
};

const SEVERITY_CONFIG = {
  [INCIDENT_SEVERITY.LOW]: {
    label: 'Baja',
    color: '#28a745',
    bgColor: 'rgba(40, 167, 69, 0.1)',
  },
  [INCIDENT_SEVERITY.MEDIUM]: {
    label: 'Media',
    color: '#ffc107',
    bgColor: 'rgba(255, 193, 7, 0.1)',
  },
  [INCIDENT_SEVERITY.HIGH]: {
    label: 'Alta',
    color: '#fd7e14',
    bgColor: 'rgba(253, 126, 20, 0.1)',
  },
  [INCIDENT_SEVERITY.CRITICAL]: {
    label: 'Critica',
    color: '#dc3545',
    bgColor: 'rgba(220, 53, 69, 0.1)',
  },
};

const STATUS_CONFIG = {
  [INCIDENT_STATUS.REPORTED]: {
    label: 'Reportado',
    color: '#6c757d',
    bgColor: 'rgba(108, 117, 125, 0.1)',
  },
  [INCIDENT_STATUS.ACKNOWLEDGED]: {
    label: 'Reconocido',
    color: '#17a2b8',
    bgColor: 'rgba(23, 162, 184, 0.1)',
  },
  [INCIDENT_STATUS.IN_PROGRESS]: {
    label: 'En Proceso',
    color: '#0d6efd',
    bgColor: 'rgba(13, 110, 253, 0.1)',
  },
  [INCIDENT_STATUS.RESOLVED]: {
    label: 'Resuelto',
    color: '#28a745',
    bgColor: 'rgba(40, 167, 69, 0.1)',
  },
  [INCIDENT_STATUS.CLOSED]: {
    label: 'Cerrado',
    color: '#495057',
    bgColor: 'rgba(73, 80, 87, 0.1)',
  },
};

/**
 * Format relative time from date
 */
const getRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) return 'Ahora mismo';
  if (diffMinutes < 60) return `Hace ${diffMinutes} min`;
  if (diffHours < 24) return `Hace ${diffHours}h`;
  if (diffDays === 1) return 'Ayer';
  if (diffDays < 7) return `Hace ${diffDays} dias`;

  return date.toLocaleDateString('es-CR', {
    day: 'numeric',
    month: 'short',
  });
};

const IncidentCard = ({ incident, onClick, compact }) => {
  const {
    title,
    incident_type,
    severity,
    status,
    reported_at,
    project,
    photos,
    latitude,
    longitude,
    work_stopped,
  } = incident;

  const TypeIcon = TYPE_ICONS[incident_type] || FiHelpCircle;
  const severityConfig = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG[INCIDENT_SEVERITY.MEDIUM];
  const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG[INCIDENT_STATUS.REPORTED];
  const typeLabel = INCIDENT_TYPE_LABELS[incident_type] || 'Otro';
  const hasPhotos = photos && photos.length > 0;
  const hasLocation = latitude && longitude;
  const relativeTime = getRelativeTime(reported_at);

  if (compact) {
    return (
      <div
        className="incident-card compact"
        onClick={() => onClick && onClick(incident)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onClick && onClick(incident)}
      >
        <div
          className="incident-severity-bar"
          style={{ backgroundColor: severityConfig.color }}
        />
        <div className="incident-card-content">
          <div className="incident-type-icon" style={{ color: severityConfig.color }}>
            <TypeIcon size={18} />
          </div>
          <div className="incident-info">
            <span className="incident-title-compact">{title}</span>
            <span className="incident-meta-compact">
              {typeLabel} - {relativeTime}
            </span>
          </div>
          <div className="incident-status-compact">
            <span
              className="status-dot"
              style={{ backgroundColor: statusConfig.color }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="incident-card"
      onClick={() => onClick && onClick(incident)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick && onClick(incident)}
    >
      {/* Severity indicator bar */}
      <div
        className="incident-severity-bar"
        style={{ backgroundColor: severityConfig.color }}
      />

      <div className="incident-card-body">
        {/* Header with badges */}
        <div className="incident-card-header">
          <span
            className="incident-severity-badge"
            style={{
              color: severityConfig.color,
              backgroundColor: severityConfig.bgColor,
            }}
          >
            {severityConfig.label}
          </span>

          <span
            className="incident-status-badge"
            style={{
              color: statusConfig.color,
              backgroundColor: statusConfig.bgColor,
            }}
          >
            {statusConfig.label}
          </span>

          {work_stopped && (
            <span className="incident-stopped-badge">
              Trabajo detenido
            </span>
          )}
        </div>

        {/* Title and type */}
        <div className="incident-title-section">
          <div className="incident-type-icon-large" style={{ color: severityConfig.color }}>
            <TypeIcon size={24} />
          </div>
          <div className="incident-title-content">
            <h4 className="incident-title">{title}</h4>
            <span className="incident-type-label">{typeLabel}</span>
          </div>
          <div className="incident-arrow">
            <FiChevronRight size={20} />
          </div>
        </div>

        {/* Footer with metadata */}
        <div className="incident-card-footer">
          <div className="incident-meta">
            <span className="incident-time">{relativeTime}</span>

            {project && (
              <span className="incident-project">
                {project.project_number || project.title}
              </span>
            )}
          </div>

          <div className="incident-indicators">
            {hasLocation && (
              <span className="incident-indicator" title="Con ubicacion GPS">
                <FiMapPin size={14} />
              </span>
            )}
            {hasPhotos && (
              <span className="incident-indicator" title={`${photos.length} foto(s)`}>
                <FiImage size={14} />
                <span className="indicator-count">{photos.length}</span>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

IncidentCard.propTypes = {
  incident: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    incident_type: PropTypes.oneOf(Object.values(INCIDENT_TYPE)).isRequired,
    severity: PropTypes.oneOf(Object.values(INCIDENT_SEVERITY)).isRequired,
    status: PropTypes.oneOf(Object.values(INCIDENT_STATUS)).isRequired,
    reported_at: PropTypes.string.isRequired,
    project: PropTypes.shape({
      id: PropTypes.string,
      project_number: PropTypes.string,
      title: PropTypes.string,
    }),
    photos: PropTypes.array,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    work_stopped: PropTypes.bool,
  }).isRequired,
  onClick: PropTypes.func,
  compact: PropTypes.bool,
};

IncidentCard.defaultProps = {
  onClick: null,
  compact: false,
};

export default IncidentCard;
